import React, { useState, useEffect } from 'react';
import api from '../api/axios';
import { useConfig } from '../context/ConfigContext';

const MEDICINE_ICONS = [
    { icon: '💊', label: 'Cápsula', bg: 'bg-[#ff5874]', border: 'border-[#ff5874]' },
    { icon: '🧪', label: 'Jarabe', bg: 'bg-[#008b8b]', border: 'border-[#008b8b]' },
    { icon: '💉', label: 'Inyección', bg: 'bg-[#00595b]', border: 'border-[#00595b]' },
    { icon: '🧫', label: 'Comprimido', bg: 'bg-[#e63946]', border: 'border-[#e63946]' }
];

const InteractivePoll = () => {
    const { config } = useConfig();
    const [poll, setPoll] = useState(null);
    const [selectedOption, setSelectedOption] = useState(null);
    const [hasVoted, setHasVoted] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [message, setMessage] = useState('');

    const primaryColor = config?.primaryColor || '#00595b';
    const secondaryColor = config?.secondaryColor || '#ff5874';
    const cardBorderColor = config?.cardBorderColor || secondaryColor;

    useEffect(() => {
        const fetchActivePoll = async () => {
            try {
                const res = await api.get('/polls/active');
                if (res.data) {
                    if (!poll || poll.id !== res.data.id) {
                        setPoll(res.data);
                        setHasVoted(res.data.hasVoted);
                        const savedOption = localStorage.getItem(`voted_poll_${res.data.id}`);
                        setSelectedOption(savedOption ? parseInt(savedOption) : null);
                        setMessage(res.data.hasVoted ? 'Ya has registrado tu voto en esta pregunta.' : '');
                    } else if (res.data.hasVoted && !hasVoted) {
                        setHasVoted(true);
                        setMessage('¡Voto registrado con éxito!');
                    }
                } else {
                    setPoll(null);
                }
            } catch (error) {
                console.error("Error fetching poll", error);
            } finally {
                setIsLoading(false);
            }
        };

        fetchActivePoll();
        const interval = setInterval(fetchActivePoll, 3000);
        return () => clearInterval(interval);
    }, [poll, hasVoted]);

    const handleVote = async (optId) => {
        if (hasVoted) return;
        setSelectedOption(optId);
        try {
            await api.post(`/polls/${poll.id}/vote`, { optionId: optId });
            setHasVoted(true);
            localStorage.setItem(`voted_poll_${poll.id}`, optId);
            setMessage('¡Voto registrado con éxito!');
        } catch (error) {
            setMessage(error.response?.data?.message || 'Error al enviar el voto');
        }
    };

    if (isLoading) return (
        <div className="flex flex-col items-center justify-center min-h-[60vh]">
            <div className="w-12 h-12 border-4 border-slate-200 border-t-[#00595b] rounded-full animate-spin"></div>
        </div>
    );

    if (!poll) return (
        <div className="animate-fadeIn max-w-lg mx-auto py-12 px-4 text-center">
            <div 
                style={{ borderColor: cardBorderColor }}
                className="bg-white rounded-3xl border-2 p-8 shadow-2xl space-y-6"
            >
                <div className="w-20 h-20 mx-auto rounded-full bg-slate-100 flex items-center justify-center text-4xl animate-bounce">
                    💊
                </div>
                <h2 style={{ color: primaryColor }} className="text-2xl font-black uppercase tracking-tight">
                    Esperando Pregunta
                </h2>
                <p className="text-xs text-slate-500 font-bold uppercase tracking-widest leading-relaxed">
                    El expositor o director activará una pregunta interactiva en breve. Mantente atento.
                </p>
            </div>
        </div>
    );

    return (
        <div className="animate-fadeIn space-y-6 max-w-2xl mx-auto pb-20 px-4 font-sans">
            {/* Header Badge & Question Card */}
            <div className="relative pt-4">
                <div className="absolute -top-1 left-1/2 -translate-x-1/2 z-20">
                    <span 
                        style={{ backgroundColor: secondaryColor }} 
                        className="px-6 py-2 rounded-xl text-white text-xs font-black uppercase tracking-widest shadow-lg inline-flex items-center gap-2"
                    >
                        <span className="w-2 h-2 rounded-full bg-white animate-ping"></span>
                        Trivia Interactiva En Vivo
                    </span>
                </div>

                <div 
                    style={{ borderColor: cardBorderColor }}
                    className="bg-white rounded-3xl border-2 pt-10 pb-8 px-6 sm:px-10 shadow-2xl text-center relative overflow-hidden"
                >
                    <h2 style={{ color: primaryColor }} className="text-xl sm:text-2xl font-black leading-snug uppercase tracking-tight">
                        {poll.question}
                    </h2>
                </div>
            </div>

            {/* Kahoot Grid Options with Medicine Symbols */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {poll.options.map((opt, idx) => {
                    const medicine = MEDICINE_ICONS[idx % MEDICINE_ICONS.length];
                    const isSelected = selectedOption === opt.id;

                    return (
                        <button
                            key={opt.id}
                            disabled={hasVoted}
                            onClick={() => handleVote(opt.id)}
                            style={{
                                backgroundColor: isSelected ? secondaryColor : undefined
                            }}
                            className={`p-5 rounded-2xl transition-all duration-300 text-left flex items-center gap-4 shadow-lg border-2 relative overflow-hidden transform active:scale-95 ${
                                isSelected
                                    ? 'text-white border-transparent scale-[1.02] ring-4 ring-rose-200'
                                    : 'bg-white border-slate-200 hover:border-slate-300 text-slate-800'
                            } ${hasVoted && !isSelected ? 'opacity-60 cursor-not-allowed' : 'hover:scale-[1.02]'}`}
                        >
                            <div className={`w-12 h-12 rounded-xl flex items-center justify-center text-2xl shrink-0 ${medicine.bg} text-white shadow-md`}>
                                {medicine.icon}
                            </div>
                            <div className="flex-1 min-w-0">
                                <span className="text-xs font-black uppercase text-slate-400 block tracking-wider mb-0.5">
                                    Opción {String.fromCharCode(65 + idx)}
                                </span>
                                <span className="font-extrabold text-sm sm:text-base leading-tight block truncate">
                                    {opt.option_text}
                                </span>
                            </div>
                            {isSelected && (
                                <span className="text-xl font-black text-white shrink-0">✓</span>
                            )}
                        </button>
                    );
                })}
            </div>

            {/* Status Message */}
            {message && (
                <div className={`p-4 rounded-2xl text-center font-black text-xs uppercase tracking-widest border shadow-md animate-fadeIn ${
                    message.includes('éxito') 
                        ? 'bg-emerald-50 text-emerald-600 border-emerald-200' 
                        : 'bg-amber-50 text-amber-700 border-amber-200'
                }`}>
                    {message}
                </div>
            )}

            {/* Helpful footer */}
            <div className="text-center pt-4">
                <p className="text-[11px] font-bold uppercase tracking-widest text-white/80">
                    {hasVoted 
                        ? 'Tu voto ha sido recibido. Los resultados se mostrarán en la pantalla principal.' 
                        : 'Selecciona una respuesta de medicamento para registrar tu opinión en tiempo real.'}
                </p>
            </div>
        </div>
    );
};

export default InteractivePoll;

